import React, { useState } from 'react';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import GroupWorkOutlinedIcon from '@mui/icons-material/GroupWorkOutlined';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  colors,
  primaryButtonSx,
  secondaryButtonSx,
  tableCellSx,
  tableHeadCellSx,
  typo,
} from '../../../../theme/tokens';
import { WriteButton, WriteIconButton } from '../../../../components/ui';
import {
  GroupInput,
  GroupScope,
  TenantGroup,
  tenantGroupsApi,
} from '../../../../api/tenantGroupsApi';
import GroupEditorDrawer from './GroupEditorDrawer';

interface Props {
  groups: TenantGroup[];
  loading: boolean;
  error: boolean;
  onNotify: (msg: string, sev: 'success' | 'error' | 'info') => void;
}

const memberSummary = (g: TenantGroup): string => {
  const counts: Record<GroupScope, number> = { customer: 0, sponsor: 0, tenant: 0 };
  g.members.forEach((m) => { counts[m.scope_type] += 1; });
  const parts: string[] = [];
  if (counts.customer) parts.push(`${counts.customer} customer${counts.customer === 1 ? '' : 's'}`);
  if (counts.sponsor) parts.push(`${counts.sponsor} sponsor${counts.sponsor === 1 ? '' : 's'}`);
  if (counts.tenant) parts.push(`${counts.tenant} tenant${counts.tenant === 1 ? '' : 's'}`);
  return parts.join(' · ');
};

/**
 * Users > Tenant Groups tab (CPT-1133). Groups bundle tenants, sponsors, or
 * customers at a read or write level (baked into the group — decisions
 * §21). Delete CASCADES the unassign (decisions §19 — the asymmetric
 * counterpart to role delete, which is BLOCKED while held): the confirm
 * dialog does a live re-check (`staleTime: 0`, mirrors a fresh read right
 * before a destructive action) so the shown count can't be stale.
 *
 * Admin ops on this tab are env-local operational RBAC data (like
 * `/api/admin/users` today), so mutations use `WriteButton`/`WriteIconButton`
 * with `ignoreEnvReadOnly`.
 */
const GroupsTable: React.FC<Props> = ({ groups, loading, error, onNotify }) => {
  const qc = useQueryClient();
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState<TenantGroup | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<TenantGroup | null>(null);

  const invalidateGroups = () => qc.invalidateQueries({ queryKey: ['tenant-groups'] });

  const createMut = useMutation({
    mutationFn: (input: GroupInput) => tenantGroupsApi.createGroup(input),
    onSuccess: () => { invalidateGroups(); setEditorOpen(false); onNotify('Group created.', 'success'); },
    onError: (err: any) => {
      const code = err?.response?.data?.error;
      onNotify(code === 'group_name_exists' ? 'A group with that name already exists.' : 'Failed to create group.', 'error');
    },
  });

  const updateMut = useMutation({
    mutationFn: (args: { id: number; input: GroupInput }) => tenantGroupsApi.updateGroup(args.id, args.input),
    onSuccess: () => {
      invalidateGroups();
      // Live reference: assigned users' derived access reflects the edit.
      qc.invalidateQueries({ queryKey: ['user-groups'] });
      setEditorOpen(false);
      setEditing(null);
      onNotify('Group updated.', 'success');
    },
    onError: (err: any) => {
      const code = err?.response?.data?.error;
      onNotify(code === 'group_name_exists' ? 'A group with that name already exists.' : 'Failed to update group.', 'error');
    },
  });

  const deleteMut = useMutation({
    mutationFn: (id: number) => tenantGroupsApi.deleteGroup(id),
    onSuccess: (res) => {
      invalidateGroups();
      qc.invalidateQueries({ queryKey: ['user-groups'] });
      setDeleteTarget(null);
      onNotify(
        res.removed_assignments > 0
          ? `Group deleted — removed from ${res.removed_assignments} user${res.removed_assignments === 1 ? '' : 's'}.`
          : 'Group deleted.',
        'success',
      );
    },
    onError: () => onNotify('Failed to delete group.', 'error'),
  });

  // Assigned-user count for the delete-confirm dialog (fetched when a target is set).
  const deleteCountQuery = useQuery({
    queryKey: ['group-assigned-count', deleteTarget?.id],
    queryFn: () => tenantGroupsApi.getAssignedUserCount(deleteTarget!.id),
    enabled: !!deleteTarget,
    // Always re-check on open — assignments can change on the Users tab
    // between opens, and a destructive confirm must not flash a stale count.
    staleTime: 0,
  });

  const openCreate = () => { setEditing(null); setEditorOpen(true); };
  const openEdit = (g: TenantGroup) => { setEditing(g); setEditorOpen(true); };

  const handleSubmit = (input: GroupInput) => {
    if (editing) updateMut.mutate({ id: editing.id, input });
    else createMut.mutate(input);
  };

  const saving = createMut.isPending || updateMut.isPending;
  const deleteCount = deleteCountQuery.data ?? 0;

  return (
    <Box>
      {/* Intro + New group */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, gap: 2 }}>
        <Typography sx={{ ...typo.secondaryText }}>
          Groups bundle tenants, sponsors, or customers at a read or write level. Assign a group to a
          user (from Manage tenant access) to grant that whole set at once — edits to a group apply to
          everyone it&apos;s assigned to.
        </Typography>
        {groups.length > 0 && (
          <WriteButton ignoreEnvReadOnly variant="contained" startIcon={<AddIcon />} onClick={openCreate} sx={{ ...primaryButtonSx, flexShrink: 0 }}>
            New group
          </WriteButton>
        )}
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress sx={{ color: colors.adminAccent }} />
        </Box>
      ) : error ? (
        <Alert severity="error" sx={{ borderRadius: '8px' }}>
          Failed to load tenant groups — try again.
        </Alert>
      ) : groups.length === 0 ? (
        <Paper elevation={0} sx={{ borderRadius: '12px', border: `1px dashed ${colors.borderDefault}`, p: 6, textAlign: 'center' }}>
          <GroupWorkOutlinedIcon sx={{ fontSize: 40, color: colors.textTertiary, mb: 1 }} />
          <Typography sx={{ ...typo.sectionTitle, mb: 0.5 }}>No tenant groups yet</Typography>
          <Typography sx={{ ...typo.secondaryText, mb: 2, maxWidth: 460, mx: 'auto' }}>
            Create a group to grant a named set of tenants, sponsors, or customers to users at a read
            or write level.
          </Typography>
          <WriteButton ignoreEnvReadOnly variant="contained" startIcon={<AddIcon />} onClick={openCreate} sx={primaryButtonSx}>
            New group
          </WriteButton>
        </Paper>
      ) : (
        <Paper elevation={0} sx={{ borderRadius: '12px', border: `1px solid ${colors.borderLight}`, overflow: 'hidden' }}>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={tableHeadCellSx}>Group</TableCell>
                  <TableCell sx={{ ...tableHeadCellSx, width: 120 }}>Level</TableCell>
                  <TableCell sx={tableHeadCellSx}>Scope</TableCell>
                  <TableCell sx={{ ...tableHeadCellSx, width: 120 }}>Members</TableCell>
                  <TableCell sx={{ ...tableHeadCellSx, width: 110, textAlign: 'center' }} />
                </TableRow>
              </TableHead>
              <TableBody>
                {groups.map((g) => (
                  <TableRow key={g.id} hover>
                    <TableCell sx={tableCellSx}>
                      <Typography sx={{ fontSize: 13, fontWeight: 700 }}>{g.name}</Typography>
                    </TableCell>
                    <TableCell sx={tableCellSx}>
                      <Chip
                        size="small"
                        label={g.level}
                        color={g.level === 'write' ? 'warning' : 'default'}
                        sx={{ height: 20, fontSize: 10, fontWeight: 700, textTransform: 'uppercase' }}
                      />
                    </TableCell>
                    <TableCell sx={tableCellSx}>
                      {/* Scope = the entities bundled IN the group (tenant_group_member
                          rows) — deliberately NOT called "Members": that word means the
                          USERS assigned to the group (next column). */}
                      <Typography sx={{ fontSize: 13, fontWeight: 600 }}>
                        {g.members.length} {g.members.length === 1 ? 'entity' : 'entities'}
                      </Typography>
                      {g.members.length > 0 && (
                        <Typography sx={{ ...typo.tinyText }}>{memberSummary(g)}</Typography>
                      )}
                    </TableCell>
                    <TableCell sx={tableCellSx}>
                      <Typography sx={{ fontSize: 13, fontWeight: 600 }}>
                        {g.assigned_user_count ?? 0} user{(g.assigned_user_count ?? 0) === 1 ? '' : 's'}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ ...tableCellSx, textAlign: 'center' }}>
                      <Tooltip title="Edit group">
                        <span>
                          <WriteIconButton
                            ignoreEnvReadOnly
                            size="small"
                            aria-label={`Edit group — ${g.name}`}
                            onClick={() => openEdit(g)}
                          >
                            <EditOutlinedIcon sx={{ fontSize: 18, color: colors.textSecondary }} />
                          </WriteIconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Delete group">
                        <span>
                          <WriteIconButton
                            ignoreEnvReadOnly
                            size="small"
                            aria-label={`Delete group — ${g.name}`}
                            onClick={() => setDeleteTarget(g)}
                          >
                            <DeleteOutlineIcon sx={{ fontSize: 18, color: colors.errorIcon }} />
                          </WriteIconButton>
                        </span>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      <GroupEditorDrawer
        open={editorOpen}
        group={editing}
        saving={saving}
        onClose={() => { if (!saving) { setEditorOpen(false); setEditing(null); } }}
        onSubmit={handleSubmit}
        onNotify={onNotify}
      />

      <Dialog open={!!deleteTarget} onClose={() => { if (!deleteMut.isPending) setDeleteTarget(null); }}>
        <DialogTitle sx={{ ...typo.sectionTitle }}>Delete group?</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontSize: 14, color: colors.textSecondary }}>
            {deleteTarget && (
              <>You&apos;re about to delete <b>{deleteTarget.name}</b>.{' '}</>
            )}
            {deleteCountQuery.isError
              ? 'Could not check how many users are assigned — proceed with caution.'
              : deleteCountQuery.isFetching
                ? 'Checking assignments…'
                : deleteCount > 0
                  ? `It's currently assigned to ${deleteCount} user${deleteCount === 1 ? '' : 's'} — they'll immediately lose the access it grants.`
                  : 'It isn’t assigned to any users.'}
            {' '}This can&apos;t be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <WriteButton ignoreEnvReadOnly onClick={() => setDeleteTarget(null)} disabled={deleteMut.isPending} variant="outlined" sx={secondaryButtonSx}>
            Cancel
          </WriteButton>
          <WriteButton
            ignoreEnvReadOnly
            onClick={() => deleteTarget && deleteMut.mutate(deleteTarget.id)}
            disabled={deleteMut.isPending}
            variant="contained"
            sx={{ ...primaryButtonSx, backgroundColor: colors.errorIcon, color: colors.bgWhite, '&:hover': { backgroundColor: colors.errorText } }}
          >
            {deleteMut.isPending ? 'Deleting…' : 'Delete group'}
          </WriteButton>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default GroupsTable;
